// School years and the season inside them.
//
// Two questions live here. Which school years the dashboard may offer -- a
// function of how far back the enrollment ledger reaches, mirrored from the
// backend's window_coverage() so the picker can be built before any request has
// returned. And when a district's year actually began, which the backend detects
// from usage and which moves every rate the pages divide by time.
//
// The prose belongs with them on purpose: a reconstructed roster and an observed
// season start are both cases of the dashboard showing a number it has to qualify,
// and the wording has to be identical wherever it appears.
//
// No React, no imports.

// ISO day stamp, in the same shape the stats endpoints take for `start`/`end`.
const isoDate = (d) => d.toISOString().slice(0, 10);

// ---- school year helpers ---------------------------------------------------
// A school year runs Aug 1 -> Jul 31, identified by its starting calendar year.
const currentSchoolYear = (today) =>
  today.getMonth() + 1 >= 8 ? today.getFullYear() : today.getFullYear() - 1;
// 2025 -> "2025–26"
const syLabel = (y) => `${y}–${String(y + 1).slice(2)}`;
// How many past school years to list when we don't yet know how long the district
// has existed — org_created arrives with the first stats response, same as
// ledger_start. Which of the listed years are *selectable* is a separate question,
// answered by how far back the enrollment ledger reaches; see syYearOptions.
const PAST_YEARS_SHOWN = 4;

// The school year a district came into existence in, which is as far back as the
// picker has any business reaching: earlier years belong to a district that did
// not exist, and offering them is how a two-year-old org ended up listing 2021–22.
//
// Clamped to the current year because a just-created org would otherwise floor the
// list above it, and null for a missing or unparseable date so callers fall back to
// PAST_YEARS_SHOWN rather than collapsing to one option.
//
// Note this is the org's row in the accounts database, not the district's founding
// or its first use of ABii. On prod 153 of ~316 orgs share 2021-08-23, the platform
// migration epoch, so for those it reads as "at least this long" — which is the
// right direction for a floor: it can list a year too many, never one too few.
const syOrgFloor = (orgCreated, curSY) => {
  if (!orgCreated) return null;
  const d = new Date(orgCreated);
  if (Number.isNaN(d.getTime())) return null;
  return Math.min(currentSchoolYear(d), curSY);
};

// A school year's window, matching the backend's parse_stats_window: Aug 1 of the
// starting year through Jul 31 of the next, except the current year, which ends
// today (there is no data past it, and the impact series is cumulative).
const syWindow = (y, curSY, today) => ({
  start: `${y}-08-01`,
  end: y === curSY ? isoDate(today) : `${y + 1}-07-31`,
});

// A window this short has no business being bucketed by month.
//
// Not a styling preference: three weeks into a school year, a monthly window is
// ONE bucket. The trajectory is a single point, the engagement mix is one column,
// the fleet strip is one bar — and that bar is the partial current month, so the
// whole strip is the case the reader can least trust. Eight weeks is the point
// where monthly starts to have enough points to read a shape from (two full
// months plus a partial), and it is also a focus cycle's own floor, so a window
// narrowed to a cycle lands on weekly for the same reason pages.js already forces
// it to.
const SHORT_WINDOW_WEEKS = 8;

// The grain a window asks for when nobody has picked one. Weekly for a short
// window, monthly otherwise.
//
// Deliberately DERIVED rather than written into the URL on the way past: it is a
// default, not a choice, so it has to re-derive as the school year grows past
// eight weeks and hand the reader monthly buckets without their view being
// pinned to a decision they never made. An explicit ?interval= still wins.
const defaultGrain = (start, end) => {
  const ms = new Date(`${end}T23:59:59`) - new Date(`${start}T00:00:00`);
  if (Number.isNaN(ms)) return "month";
  return ms / (7 * 86400000) <= SHORT_WINDOW_WEEKS ? "week" : "month";
};

// How much of a school year the enrollment ledger can account for. Mirrors the
// backend's window_coverage() so the picker can be built before any request has
// come back, and agrees with the `coverage` field once one has.
//
//   "full"    — the ledger predates the year; every roster change was observed.
//   "partial" — the year straddles the ledger's start. The roster is
//               reconstructed from the cutover snapshot forward: it holds
//               everyone still enrolled when the ledger began plus every change
//               since, but not learners who left before that. Usable, and
//               labelled as reconstructed.
//   "none"    — the year ended before the ledger began. Nothing to reconstruct
//               from, so the year isn't offered at all; showing it would mean
//               charting today's roster against a stale year's events.
const syCoverage = (y, ledgerStart, curSY, today) => {
  if (!ledgerStart) return "none";
  const ls = new Date(ledgerStart);
  if (Number.isNaN(ls.getTime())) return "none";
  const { start, end } = syWindow(y, curSY, today);
  if (new Date(`${end}T23:59:59`) < ls) return "none";
  return new Date(`${start}T00:00:00`) >= ls ? "full" : "partial";
};

// Options for the school-year picker, newest first.
//
// Two independent bounds, and they answer different questions. How far back the
// list *reaches* is the district's own lifetime (`orgCreated`) — a year before the
// org existed is not a year to offer at all. Which of those years are *selectable*
// is `ledgerStart`: a listed-but-uncovered year stays visible and disabled, which
// is the point of listing it. Without the first bound the list is a fixed four
// years back, wrong in both directions at once — offering 2021–22 to a district
// created in 2025, and stopping at 2022–23 for one that predates it.
//
// Both arrive with the first stats response, so on the very first render neither is
// known: only the current year is selectable and the list falls back to
// PAST_YEARS_SHOWN. That self-corrects one request later, and a URL-seeded year
// (`?start=`) loads regardless of what the picker offers — the picker gates
// browsing, not addressability, so a shared link to a past year never depends on
// this having resolved.
const syYearOptions = (curSY, schoolYear, ledgerStart, today, orgCreated) => {
  const floor = syOrgFloor(orgCreated, curSY);
  const lo = Math.min(
    floor === null ? curSY - PAST_YEARS_SHOWN : floor,
    schoolYear
  );
  const opts = [];
  for (let y = curSY; y >= lo; y--) {
    // The current year is derived like any other, except that before the first
    // response has reported ledger_start there is nothing to derive from and
    // syCoverage would say "none" — the live year is never that.
    const coverage =
      y === curSY && !ledgerStart
        ? "full"
        : syCoverage(y, ledgerStart, curSY, today);
    // "unavailable" always means unselectable, including for a year reached by
    // URL rather than by picking it — a <select> still displays a disabled
    // option as its value, so such a year stays visible without pretending to be
    // an offer. The live year is never disabled.
    const disabled = coverage === "none" && y !== curSY;
    let suffix = "";
    if (y === curSY) suffix = " (current)";
    else if (coverage === "partial") suffix = " (reconstructed)";
    else if (coverage === "none") suffix = " — unavailable";
    opts.push({ year: y, coverage, disabled, label: syLabel(y) + suffix });
  }
  return opts;
};

// Footnote shown wherever roster-dependent numbers are drawn for a reconstructed
// year. Kept here so every page words the caveat identically.
const RECONSTRUCTED_NOTE =
  "Rosters for this school year are reconstructed from enrollment as it stood " +
  "at year end. Learners who transferred or were removed mid-year may be " +
  "missing, so counts can read slightly low.";

// ---- observed season start -------------------------------------------------
// The backend detects when a district's year actually began, from its own usage,
// and scores dosage and the out-of-session mask from that date instead of a
// nominal August ramp (see vanroboapp/observed_season.py). Districts diverge by
// five months on that date, so it moves the numbers materially — which is why it
// is labelled rather than applied silently.
const fmtSeasonDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
};

// Two sentences for the "How to read this" trays, returning null when there is
// nothing to say so callers can append them without knowing the reason codes.
//
// Deliberately NOT a banner alongside the reconstructed-year caveat. That one is
// a warning — the roster is known to be incomplete and every number on the page
// inherits a direction of error. This is the opposite: the calendar is MORE
// accurate than the nominal default, and it only affects the panels that divide
// by time. A banner would give an improvement the visual weight of a defect, and
// on a district whose year simply started in August it would say almost nothing.
// So it goes where a reader who is questioning a specific number will look.
//
// Split in two because the panels ask different questions of the same date:
// dosage panels want to know what the divisor was, the time-bucketed strips want
// to know why early buckets are shaded.

// For any panel showing lessons/week or a dosage tier.
const seasonRateDetail = (season) => {
  if (!season) return null;
  if (season.reason === "observed" && season.start) {
    const when = fmtSeasonDate(season.start);
    return when
      ? `Weekly rates are measured from ${when}, when this district's usage ` +
          `actually began, rather than from a nominal August start — time before ` +
          `a district gets going is opportunity it never had, so counting it ` +
          `would understate every rate here.`
      : null;
  }
  // The interesting case: a district whose first real usage was in January is a
  // low adopter rather than a late starter, so the backend deliberately does NOT
  // shorten its denominator. Saying so beats silence, because the other reading
  // of a poor dosage number is "the dashboard doesn't know when we started".
  if (season.reason === "after_cap" && season.detected) {
    const when = fmtSeasonDate(season.detected);
    return when
      ? `First substantial usage this year was ${when}. Rates are still measured ` +
          `across the whole school year: a start that late reflects adoption ` +
          `rather than the calendar, so shortening the divisor to match would ` +
          `flatter it.`
      : null;
  }
  return null;
};

// For any panel that shades or greys out-of-session time buckets.
const seasonShadeDetail = (season) => {
  if (!season || season.reason !== "observed" || !season.start) return null;
  const when = fmtSeasonDate(season.start);
  return when
    ? `Buckets before ${when} — this district's own start of usage — are shaded ` +
        `for the same reason as summer: a district that has not started its year ` +
        `yet is not a district that has stopped.`
    : null;
};

// Shown instead of the numbers for a year with no enrollment history at all.
// Rendering the panels anyway would chart this year's roster against that year's
// events — plausible-looking and wrong, which is worse than an empty page.
const NO_HISTORY_MESSAGE =
  "Enrollment history doesn't reach this school year, so its numbers can't be " +
  "attributed to the schools, classes and grades that existed at the time. " +
  "Pick a more recent school year.";


export {
  currentSchoolYear,
  syLabel,
  syOrgFloor,
  syWindow,
  defaultGrain,
  SHORT_WINDOW_WEEKS,
  syCoverage,
  syYearOptions,
  RECONSTRUCTED_NOTE,
  seasonRateDetail,
  seasonShadeDetail,
  NO_HISTORY_MESSAGE,
};
